// ÖSSZEG-BLOKK: NETTÓ nagyban (a vezető szám), alatta áfa, majd bruttó — jobbra igazítva, monospace tabuláris számokkal.
//
// MIÉRT KÖZÖS RÉTEG (`docs/foundations/design.md` §9 „shared jelölt"): a pénz-oszlop akkor
// olvasható, ha a szám-karakterek EGY vonalban állnak (tabular-nums) és a bruttó vizuálisan vezet. Ez a
// szabály nem partner-specifikus — a belső számla-listák is ezt kérik; ott a 2. használatnál kapcsolható be.
//
// A DEVIZA MINDIG ott van: összeg deviza nélkül értelmezhetetlen (a repo adat-szabálya), és a partnernek
// EUR-os tétele is lehet.
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

/** Decimal-STRING + deviza → lokalizált pénz-formátum (a HUF-ot tizedes nélkül írjuk, ahogy a legacy is). */
export function formatMoney(amount: string, currency: string, language: string): string {
  const n = Number(amount);
  if (!Number.isFinite(n)) return `${amount} ${currency}`;
  return new Intl.NumberFormat(language.toLowerCase().startsWith('en') ? 'en-GB' : 'hu-HU', {
    style: 'currency',
    currency,
    maximumFractionDigits: currency === 'HUF' ? 0 : 2,
  }).format(n);
}

export interface MoneyBlockProps {
  /** Bruttó — elhagyható (pl. terv-tételnél csak nettó van). */
  gross?: string | undefined;
  net?: string | undefined;
  vat?: string | undefined;
  currency: string;
  className?: string;
}

export function MoneyBlock({ gross, net, vat, currency, className }: MoneyBlockProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const primary = net ?? gross;
  if (primary === undefined) return <></>;
  const fmt = (v: string): string => formatMoney(v, currency, i18n.language);
  const sub = 'text-muted-foreground font-mono text-[11px] tabular-nums';
  return (
    <div className={cn('flex flex-col items-end gap-px', className)}>
      {/* a vezető szám a nettó (ha nincs, a bruttó marad egyedül) */}
      <span
        data-testid="money-primary"
        className="font-mono text-[15px] font-semibold tabular-nums tracking-[-0.02em]"
      >
        {fmt(primary)}
      </span>
      {vat !== undefined && (
        <span className={sub}>
          {t('money.vat')} {fmt(vat)}
        </span>
      )}
      {net !== undefined && gross !== undefined && (
        <span className={sub}>
          {t('money.gross')} {fmt(gross)}
        </span>
      )}
    </div>
  );
}
